import { useCallback, useEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Save-as-you-go feedback: call `flash()` after a successful write and the
 * tick shows for a couple of seconds.
 */
export function useSavedTick(durationMs = 2000) {
  const [visible, setVisible] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const flash = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    setVisible(true)
    timer.current = setTimeout(() => setVisible(false), durationMs)
  }, [durationMs])

  return { visible, flash }
}

/** The "✓ Saved" label. Reserve its space so the heading line doesn't jump. */
export function SavedTick({ visible }: { visible: boolean }) {
  const { t } = useTranslation('settings')
  return (
    <span
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-1 text-xs font-medium text-emerald-600 transition-opacity duration-300 dark:text-emerald-400 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {visible && (
        <>
          <Check className="size-3.5" aria-hidden />
          {t('saveBar.saved')}
        </>
      )}
    </span>
  )
}
